import { useEventsStore } from '~/stores/events'
import { mapEvents, mapEvent, mapBasicEventInformation, mapQrStyle } from '../mappers/event-mapper'
import { GUEST_PARTY_AUTH_COOKIE_KEY } from '../constants/cookies'
import { REJECT } from '../constants/reject-reasons'
import { useGuestStore } from '~/stores/guest'
import { toQrStylePayload } from '~/lib/qr/qr-style'

/**
 * @typedef {EventService}
 * @alias this.$eventService
 */
export class EventService {
  constructor(nuxtApp, { apiService, clientCookieService }) {
    this.config = nuxtApp.$config.public
    this.route = useRoute()
    this.guestStore = useGuestStore()
    this.apiService = apiService
    this.clientCookieService = clientCookieService
  }

  /**
   * @param {momentshare.models.event.EventProperty[]} eventProperties
   * @param {string} key
   * @returns {momentshare.models.event.EventProperty|undefined}
   */
  getEventPropertyByKey(eventProperties, key) {
    if (!eventProperties) return

    return eventProperties.find((property) => property.key === key)
  }

  /**
   * @returns {Promise<momentshare.models.event.Event[]>}
   */
  getUserEvents() {
    return this.apiService.instance.get(`/event`).then((response) => mapEvents(response.data, this.config.filesPath))
  }

  /**
   * @returns {Promise<void>}
   */
  async fetchUserEvents() {
    const eventsStore = useEventsStore()
    const events = await this.getUserEvents()

    eventsStore.setEvents(events)
  }

  /**
   * @param {string} eventId
   * @returns {Promise<momentshare.models.event.Event>}
   */
  getEventAsUser(eventId) {
    return this.apiService.instance
      .get(`/event/${eventId}/as-user`)
      .then((response) => mapEvent(response.data, this.config.filesPath))
  }

  getEventBackgroundImageState(eventId) {
    return this.apiService.instance.get(`/event/${eventId}/background-image-state`).then((response) => response.data)
  }

  /**
   * @param {string} eventId
   * @returns {Promise<{status: string, migratedCount: number, totalFiles: number}>}
   */
  async getMediaMigrationStatus(eventId) {
    const authHeaders = await this.getGuestEventAuthHeaders(eventId)

    return this.apiService.instance
      .get(`/event/${eventId}/migration-status`, {
        headers: {
          ...(authHeaders && { ...authHeaders }),
        },
      })
      .then((response) => response.data)
  }

  /**
   * Raw guest basic-information fetch — returns the plain API payload so it can
   * survive Nuxt payload serialization (useAsyncData). Map with
   * `mapBasicEventInformation` on the consuming side.
   * @param {string} eventId
   * @param {string|null} [token]
   * @returns {Promise<Object>}
   */
  async getBasicEventInformationRaw(eventId, token = null) {
    const guestAuthToken = token
      ? this.guestEventAuthorizationHeaderFromCookie(eventId, token)
      : await this.getGuestEventAuthHeaders(eventId)

    if (!guestAuthToken) {
      return Promise.reject(REJECT.UNAUTHORIZED)
    }

    return this.apiService.instance
      .get(`/event/basic-information/${eventId}`, {
        headers: {
          ...guestAuthToken,
        },
      })
      .then((response) => response.data)
  }

  /**
   * @param {string} eventId
   * @param {string|null} [token]
   * @returns {Promise<momentshare.models.event.Event>}
   */
  async getBasicEventInformation(eventId, token) {
    const basicEventInformation = await this.getBasicEventInformationRaw(eventId, token)

    return mapBasicEventInformation(basicEventInformation)
  }

  /**
   * @param {string} eventId
   * @param {Object} formData
   * @returns {Promise}
   */
  updateEvent(eventId, formData) {
    return this.apiService.instance.patch(`/event/${eventId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  /**
   * @param {string} eventId
   * @returns {Promise}
   */
  deleteEvent(eventId) {
    return this.apiService.instance.delete(`/event/${eventId}`)
  }

  /**
   * PUT the host's QR style. The API validates and answers the stored style;
   * the answer (not our local copy) lands in the store.
   * @param {string} eventId
   * @param {QrStyle} style
   * @returns {Promise<QrStyle>}
   */
  saveQrStyle(eventId, style) {
    return this.apiService.instance
      .put(`/event/${eventId}/qr-style`, toQrStylePayload(style))
      .then((response) => this.storeQrStyle(eventId, response.data))
  }

  /**
   * Upload the centre photo (jpeg/png/webp/heic, max 10 MB). The API crops it to
   * 512x512, stores it next to the event files and switches the style to kind photo.
   * @param {string} eventId
   * @param {File} file
   * @returns {Promise<QrStyle>}
   */
  uploadQrPhoto(eventId, file) {
    const formData = new FormData()
    formData.append('photo', file)

    return this.apiService.instance
      .post(`/event/${eventId}/qr-style/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => this.storeQrStyle(eventId, response.data))
  }

  /**
   * @param {string} eventId
   * @returns {Promise<QrStyle>}
   */
  removeQrPhoto(eventId) {
    return this.apiService.instance
      .delete(`/event/${eventId}/qr-style/photo`)
      .then((response) => this.storeQrStyle(eventId, response.data))
  }

  /** @private */
  storeQrStyle(eventId, raw) {
    const style = mapQrStyle(raw, this.config.filesPath)

    useEventsStore().setEventQrStyle(eventId, style)

    return style
  }

  /**
   * Give an album its own QR style (a copy of the event's current style). The API
   * answers the stored style.
   * @param {string} eventId
   * @param {string} albumId
   * @returns {Promise<QrStyle>}
   */
  createAlbumQrStyle(eventId, albumId) {
    return this.apiService.instance
      .post(`/event/${eventId}/albums/${albumId}/qr-style`)
      .then((response) => this.storeAlbumQrStyle(eventId, albumId, response.data))
  }

  /**
   * PUT the album's own QR style; the API's answer lands in the store.
   * @param {string} eventId
   * @param {string} albumId
   * @param {QrStyle} style
   * @returns {Promise<QrStyle>}
   */
  saveAlbumQrStyle(eventId, albumId, style) {
    return this.apiService.instance
      .put(`/event/${eventId}/albums/${albumId}/qr-style`, toQrStylePayload(style))
      .then((response) => this.storeAlbumQrStyle(eventId, albumId, response.data))
  }

  /**
   * Upload the album's centre photo (same rules as uploadQrPhoto).
   * @param {string} eventId
   * @param {string} albumId
   * @param {File} file
   * @returns {Promise<QrStyle>}
   */
  uploadAlbumQrPhoto(eventId, albumId, file) {
    const formData = new FormData()
    formData.append('photo', file)

    return this.apiService.instance
      .post(`/event/${eventId}/albums/${albumId}/qr-style/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => this.storeAlbumQrStyle(eventId, albumId, response.data))
  }

  /**
   * @param {string} eventId
   * @param {string} albumId
   * @returns {Promise<QrStyle>}
   */
  removeAlbumQrPhoto(eventId, albumId) {
    return this.apiService.instance
      .delete(`/event/${eventId}/albums/${albumId}/qr-style/photo`)
      .then((response) => this.storeAlbumQrStyle(eventId, albumId, response.data))
  }

  /**
   * Remove the album's own style so it follows the event again.
   * @param {string} eventId
   * @param {string} albumId
   * @returns {Promise<null>}
   */
  clearAlbumQrStyle(eventId, albumId) {
    return this.apiService.instance.delete(`/event/${eventId}/albums/${albumId}/qr-style`).then(() => {
      useEventsStore().setAlbumQrStyle(eventId, albumId, null)

      return null
    })
  }

  /** @private */
  storeAlbumQrStyle(eventId, albumId, raw) {
    const style = mapQrStyle(raw, this.config.filesPath)

    useEventsStore().setAlbumQrStyle(eventId, albumId, style)

    return style
  }

  /**
   * Owner: list the albums of an event.
   * apiKey + owner Authorization are injected by the axios instance interceptors.
   * @param {string} eventId
   * @returns {Promise<Array<{id: string, name: string, slug: string, shortLink: string}>>}
   */
  getAlbums(eventId) {
    return this.apiService.instance.get(`/event/${eventId}/albums`).then((response) => response.data)
  }

  /**
   * Owner: create a new album.
   * @param {string} eventId
   * @param {string} name
   * @returns {Promise}
   */
  createAlbum(eventId, name) {
    return this.apiService.instance
      .post(`/event/${eventId}/albums`, { name: typeof name === 'string' ? name.trim() : name })
      .then((response) => response.data)
  }

  /**
   * Owner: update an album — rename and/or set its sorting override.
   * `gallerySorting: null` clears the override (inherit the event's setting).
   * @param {string} eventId
   * @param {string} albumId
   * @param {{ name?: string, gallerySorting?: string|null }} payload
   * @returns {Promise}
   */
  updateAlbum(eventId, albumId, payload = {}) {
    const body = {
      ...(payload.name !== undefined && {
        name: typeof payload.name === 'string' ? payload.name.trim() : payload.name,
      }),
      ...(payload.gallerySorting !== undefined && { gallerySorting: payload.gallerySorting }),
      ...(payload.isPrivate !== undefined && { isPrivate: payload.isPrivate }),
    }

    return this.apiService.instance.patch(`/event/${eventId}/albums/${albumId}`, body).then((response) => response.data)
  }

  /**
   * Owner: delete an album. `media` decides the fate of the album's photos:
   *  - 'keep'   → photos move back to the main event (albumId unset).
   *  - 'delete' → photos (docs + files) are destroyed.
   * @param {string} eventId
   * @param {string} albumId
   * @param {'keep'|'delete'} media
   * @returns {Promise}
   */
  deleteAlbum(eventId, albumId, media = 'keep') {
    return this.apiService.instance.delete(`/event/${eventId}/albums/${albumId}?media=${media}`)
  }

  /**
   * @param {string} eventId
   * @param {string} [name] optional custom backup label; empty/whitespace falls back to server default
   * @returns {Promise}
   */
  resetEvent(eventId, name) {
    const trimmed = typeof name === 'string' ? name.trim() : ''
    const payload = trimmed ? { name: trimmed } : {}

    return this.apiService.instance.post(`/event/${eventId}/reset`, payload)
  }

  /**
   * Guest: resolve an album SHORT TOKEN to its album within the event. The
   * only way a guest can learn about a PRIVATE album — the guest event
   * payload no longer lists them, so possession of the unguessable token
   * (QR / momnt.sh link) is the capability. 404s on unknown tokens and on
   * other events' albums.
   * @param {string} eventId
   * @param {string} token
   * @returns {Promise<Object>} raw album payload ({ id, name, slug, isPrivate, ... })
   */
  async resolveAlbumByToken(eventId, token) {
    const guestAuthToken = await this.getGuestEventAuthHeaders(eventId)

    if (!guestAuthToken) {
      return Promise.reject(REJECT.UNAUTHORIZED)
    }

    return this.apiService.instance
      .get(`/event/${eventId}/albums/resolve/${encodeURIComponent(token)}`, {
        headers: {
          ...guestAuthToken,
        },
      })
      .then((response) => response.data)
  }

  /**
   * Raw guest event fetch — returns the plain API payload so it can survive Nuxt
   * payload serialization (useAsyncData). Map with `mapEvent` on the consuming
   * side; a serialized `Event` model instance would lose its methods/getters.
   * @param {string} eventId
   * @param {string|null} [token]
   * @returns {Promise<Object>}
   */
  async getEventAsGuestRaw(eventId, token = null) {
    const guestAuthToken = token
      ? this.guestEventAuthorizationHeaderFromCookie(eventId, token)
      : await this.getGuestEventAuthHeaders(eventId)

    if (!guestAuthToken) {
      return Promise.reject(REJECT.UNAUTHORIZED)
    }

    return this.apiService.instance
      .get(`/event/${eventId}`, {
        headers: {
          ...guestAuthToken,
        },
      })
      .then((response) => response.data)
  }

  /**
   * @param {string} eventId
   * @param {string|null} [token]
   * @returns {Promise<momentshare.models.event.Event>}
   */
  async getEventAsGuest(eventId, token = null) {
    const event = await this.getEventAsGuestRaw(eventId, token)

    return mapEvent(event, this.config.filesPath)
  }

  /**
   * @param {string} eventId
   * @returns {undefined|string}
   */
  guestAuthTokenForEvent(eventId) {
    return this.clientCookieService.get(`${GUEST_PARTY_AUTH_COOKIE_KEY}_${eventId}`)
  }

  /**
   * @param {string} guestAccessToken
   * @returns {Promise}
   */
  getGuestAuthToken(guestAccessToken) {
    return this.apiService.instance.post(`/event/auth/guest`, { guestAccessToken }).then((response) => {
      this.setGuestEventToken(response.data)

      return response.data.token
    })
  }

  /**
   * @param {string} eventAuthToken
   * @returns {void}
   */
  setGuestEventToken(eventAuthToken) {
    this.clientCookieService.set(`${GUEST_PARTY_AUTH_COOKIE_KEY}_${eventAuthToken.id}`, eventAuthToken.token, {
      expires: 7,
      secure: true,
      sameSite: 'None',
      domain: ['localhost'].includes(window?.location?.hostname) ? 'localhost' : '.momentshare.io',
    })
  }

  /**
   * @param {Date} date
   * @returns {number}
   */
  getSafeDateTimeEpochForEvent(date) {
    // Add 8 hours to prevent wrong date in different timezones
    // TODO: Make this based on timezones
    const newDate = date.setHours(date.getHours() + 8)

    return new Date(newDate).getTime()
  }

  /**
   * @param {string} eventId
   * @param {string|null} token
   * @returns {Object}
   */
  guestEventAuthorizationHeaderFromCookie(eventId, token = null) {
    let guestAuthToken = token ? token : this.guestStore.getGuestEventToken(eventId)

    if (!guestAuthToken) {
      guestAuthToken = this.clientCookieService.get(`${GUEST_PARTY_AUTH_COOKIE_KEY}_${eventId}`)
    }

    return guestAuthToken ? { 'Event-Authorization': guestAuthToken } : undefined
  }

  async getGuestEventAuthHeaders(eventId) {
    let authHeaders = this.guestEventAuthorizationHeaderFromCookie(eventId)

    if (!authHeaders && this.route.query.auth) {
      await this.getGuestAuthToken(this.route.query.auth)
      authHeaders = this.guestEventAuthorizationHeaderFromCookie(eventId)
    }

    return authHeaders ?? null
  }
}
